import Renderable from '../../../Component/Renderable';
import UITextComponent from '../../../Component/UI/Text';
import Game from '../../../Game';
import GameObject from '../../../GameObject/GameObject';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import { IGameState } from '../../../State/Game/IGameState';
import { MissionType } from '../../../State/Player/IMission';
import { UIActions } from '../../../State/UI/UIActions';
import { UICrumbType } from '../../../State/UI/UICrumb';
import { AUDIO_UI_BUTTON_DEFAULT } from '../../Audio';
import PIXIRenderer from '../../Render/PIXIRenderer';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import BaseUISystem from '../BaseUISystem';
import SectionBaseHandler from '../Section/SectionBaseHandler';
import SectionCardsHandler from '../Section/SectionCardsHandler';
import SectionMissionHandler from '../Section/SectionMissionsHandler';
import SectionSocialHandler from '../Section/SectionSocialHandler';
import { applyDefaultButtonAnimation } from '../UIHelper';
import BaseMenu, { MenuState } from './BaseMenu';

const MAIN = 'main_container';
const SECTIONS = MAIN + '/sections';

export default class SideMenuHandler extends BaseMenu {
    private currentTabIndex: number;
    private currentSectionHandler: SectionBaseHandler;
    private tabs: GameObject[];

    constructor(system: BaseUISystem) {
        super('sideMenu', system);

        this.alwaysVisible = true;

        this.clipShow = 'SideMenuShow';
        this.clipHide = 'SideMenuHide';
    }

    public handleStateChanged(state: IGameState): void {
        // Update all tabs notification.
        this.updateNotification(
            this.tabs[0],
            this.getMissionsNotification(state)
        );
        this.updateNotification(
            this.tabs[1],
            this.getBackcardNotification(state)
        );

        // Handle state change only if there is a open section.
        if (this.currentSectionHandler && this.currentTabIndex !== -1) {
            this.currentSectionHandler.handleStateChanged(state);
        }
    }

    protected onCreate(): void {
        // Setup main tabs.
        this.currentTabIndex = -1;
        this.tabs = [
            this.element.find(`${MAIN}/missions_tab`),
            this.element.find(`${MAIN}/cards_tab`),
        ];
        this.tabs.forEach((tab: GameObject, index: number) => {
            applyDefaultButtonAnimation(tab, AUDIO_UI_BUTTON_DEFAULT);
            tab.on(
                GameObjectEvent.SELECTED,
                this.onSelectTab.bind(this, index),
                this
            );
        });
    }

    protected onDestroy(): void {
        this.tabs.forEach((tab: GameObject) => {
            tab.off(GameObjectEvent.SELECTED);
        });
    }

    protected onShow(): void {
        this.loadSection();
    }

    protected onShown(params?: any): void {
        this.system.overlayPopup.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.hidePopup(this.name));
        });
    }

    protected onHide(params?: any): void {
        this.system.overlayPopup.off(GameObjectEvent.SELECTED);
        this.currentTabIndex = -1;
    }

    protected onHidden(params?: any): void {
        this.enableInteractables(true);
        if (this.currentSectionHandler) {
            this.currentSectionHandler.destroy();
            this.currentSectionHandler = null;
        }
    }

    private onSelectTab(tabIndex: number): void {
        if (this.currentTabIndex !== tabIndex) {
            // Bring main container.
            this.system.scene.emit(
                RenderSystemEvent.BRING_TO_FRONT,
                this.element.find(SECTIONS)
            );

            // Bring selected tab to fron.
            this.system.scene.emit(
                RenderSystemEvent.BRING_TO_FRONT,
                this.tabs[tabIndex]
            );
        }

        if (this.currentState === MenuState.OPEN) {
            // If the menu is open and the user selects the visible tab, close the menu.
            if (this.currentTabIndex === tabIndex) {
                this.system.state.dispatch(UIActions.hidePopup(this.name));
                return;
            } else {
                // Otherwise load the selected tab.
                this.currentTabIndex = tabIndex;
                this.loadSection();
            }

            // Sync the state to the open menu.
            // TODO: Make the visible menu dependent on the state change.
            this.system.state.dispatch(
                UIActions.openMenu(
                    [
                        UICrumbType.SOCIAL_FRIENDS,
                        UICrumbType.MISSIONS,
                        UICrumbType.CARDS,
                    ][tabIndex]
                )
            );
        } else {
            // If the menu is closed then open the menu to the selected tab.
            this.currentTabIndex = tabIndex;
            this.system.state.dispatch(
                UIActions.showPopup(
                    this.name,
                    null,
                    [
                        UICrumbType.SOCIAL_FRIENDS,
                        UICrumbType.MISSIONS,
                        UICrumbType.CARDS,
                    ][tabIndex]
                )
            );
        }
    }

    private loadSection(): void {
        if (this.currentSectionHandler) {
            this.currentSectionHandler.destroy();
            this.currentSectionHandler = null;
        }

        if (this.currentTabIndex === 0) {
            this.currentSectionHandler = new SectionMissionHandler(this);
        } else if (this.currentTabIndex === 1) {
            this.currentSectionHandler = new SectionCardsHandler(this);
        }

        this.resizeSection(this.currentSectionHandler.section);
    }

    // tslint:disable-next-line: member-ordering
    public resizeSection(section?: GameObject): void {
        if (section) {
            section.getComponent(Renderable).visible = false;
        }
        Game.ticker.setImmediate(() => {
            this.element.container.resize(
                PIXIRenderer.renderer.width,
                PIXIRenderer.renderer.height
            );
            if (section && !section.destroyed) {
                section.getComponent(Renderable).visible = true;
            }
        });
    }

    private updateNotification(tab: GameObject, value: number): void {
        const notification = tab.find('notification');
        if (value > 0) {
            notification.getComponent(Renderable).visible = true;
            notification
                .find('text')
                .getComponent(UITextComponent).text = value.toString();
        } else {
            notification.getComponent(Renderable).visible = false;
        }
    }

    private getSocialNotification(state: IGameState): number {
        return 0;
    }

    private getMissionsNotification(state: IGameState): number {
        return state.playerState.missions.filter(
            x => x.type !== MissionType.EMPTY && x.progress === x.target
        ).length;
    }

    private getBackcardNotification(state: IGameState): number {
        return state.playerState.backcards.filter(x => x === 1).length;
    }
}
